// Workpapers (Workspace 2, screen 5): ONE index of every workpaper the engagement has or is expected to have (wp_index) —
// counts, saved views (All, Mine, Needs my sign-off, Blocked, Open questions, New), "Group by" Phase | Area | Type | Owner |
// Status, and rows with ref, name (a New badge; an agent's or a person's suggestion with its reason and Keep / Dismiss for a
// manager), type, the engine's status, sign-off circles and what the workpaper waits on.
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "../../app/context";
import { useEngine } from "../../app/engine";
import { Button, EmptyState, LoadingRows, StatusPill } from "../../components";
import type { WpIndexRow } from "../../data/types";
import { mayRequest } from "../../lib/commands";
import { WP_VIEWS, groupWorkpapers, inView, wpTypeLabel, type WpGroup, type WpView } from "../../lib/workspace";
import { Chips, Segmented, SignMarks, WaitIcons, WaitLegend, useWs, useWsAct } from "./common";

const GROUPS: Array<{ id: WpGroup; label: string }> = [
  { id: "phase", label: "Phase" }, { id: "area", label: "Area" }, { id: "type", label: "Type" }, { id: "owner", label: "Owner" }, { id: "status", label: "Status" },
];

export function WsWorkpapers() {
  const ws = useWs();
  const { me } = useApp();
  const [params, setParams] = useSearchParams();
  const view = (WP_VIEWS.some((v) => v.id === params.get("view")) ? params.get("view") : "all") as WpView;
  const group = (GROUPS.some((g) => g.id === params.get("group")) ? params.get("group") : "phase") as WpGroup;
  const set = (k: string, v: string, dflt: string) => { const p = new URLSearchParams(params); if (v === dflt) p.delete(k); else p.set(k, v); setParams(p, { replace: true }); };
  const signoffRefs = new Set(ws.attention.filter((a) => a.tab === "approval" && a.subkind === "signoff" && a.waitsOn.name === me.personName && a.ref).map((a) => a.ref!));
  const all = ws.wp;
  const rows = all.filter((w) => inView(w, view, me.personName, signoffRefs));
  const counts = [
    ["Total", all.length], ["Final", all.filter((w) => w.status === "final").length], ["In review", all.filter((w) => w.status === "in_review" || w.status === "ready_for_review").length],
    ["Blocked", all.filter((w) => w.status === "needs_input").length], ["New this week", all.filter((w) => w.isNew).length],
  ] as const;

  return (
    <div className="space-y-4">
      <header><h1 className="text-xl font-semibold">Workpapers</h1><p className="text-sm text-muted">Every workpaper this engagement has or is expected to have.</p></header>
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5" aria-label="Workpaper counts">
        {counts.map(([k, v]) => (
          <div key={k} className="rounded-lg border border-line bg-raised px-3 py-2"><dt className="text-xs text-muted">{k}</dt><dd className="text-xl font-semibold tabular-nums">{v}</dd></div>
        ))}
      </dl>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Chips label="Views" value={view} onChange={(v) => set("view", v, "all")}
          options={WP_VIEWS.map((v) => ({ id: v.id, label: v.label, count: all.filter((w) => inView(w, v.id, me.personName, signoffRefs)).length }))} />
        <Segmented label="Group by" value={group} onChange={(g) => set("group", g, "phase")} options={GROUPS} />
      </div>
      <WaitLegend />
      {ws.loading ? <LoadingRows rows={6} label="Loading workpapers" /> : rows.length === 0 ? (
        <EmptyState title="No workpapers in this view.">Try another view, or All.</EmptyState>
      ) : groupWorkpapers(rows, group).map(([g, list]) => (
        <section key={g} aria-label={`${g}, ${list.length}`} className="overflow-hidden rounded-lg border border-line bg-raised">
          <h2 className="border-b border-line bg-sunken px-3 py-1.5 text-xs font-semibold text-muted">{g} · {list.length}</h2>
          <table className="hidden w-full table-fixed text-sm md:table">
            <thead className="text-left text-xs text-muted"><tr>
              <th className="w-28 px-3 py-1.5 font-medium">Ref</th><th className="px-3 py-1.5 font-medium">Workpaper</th><th className="w-28 px-3 py-1.5 font-medium">Type</th>
              <th className="w-36 px-3 py-1.5 font-medium">Status</th><th className="w-36 px-3 py-1.5 font-medium">Sign-offs</th><th className="w-24 px-3 py-1.5 font-medium">Waiting</th></tr></thead>
            <tbody className="divide-y divide-line">
              {list.map((w) => (
                <tr key={w.ref} className={`align-top ${w.origin === "suggested" && w.suggestion?.state === "pending" ? "bg-agent-bg/40" : ""}`}>
                  <td className="px-3 py-2 font-mono text-xs">{w.ref}</td>
                  <td className="px-3 py-2"><WpName w={w} /></td>
                  <td className="px-3 py-2 text-xs text-muted">{wpTypeLabel(w.type)}</td>
                  <td className="px-3 py-2"><StatusPill state={w.status} label={w.statusLabel} /></td>
                  <td className="px-3 py-2"><SignMarks s={w.signoffs} /></td>
                  <td className="px-3 py-2"><WaitIcons w={w} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="divide-y divide-line md:hidden">
            {list.map((w) => (
              <li key={w.ref} className="space-y-1.5 px-3 py-2.5 text-sm">
                <WpName w={w} showRef />
                <div className="flex flex-wrap items-center gap-2"><StatusPill state={w.status} label={w.statusLabel} /><span className="text-xs text-muted">{wpTypeLabel(w.type)}</span><SignMarks s={w.signoffs} /><WaitIcons w={w} /></div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function WpName({ w, showRef }: { w: WpIndexRow; showRef?: boolean }) {
  const ws = useWs();
  const { catalogue, me } = useApp();
  const request = useEngine();
  const pending = w.origin === "suggested" && w.suggestion?.state === "pending";
  const may = (c: string) => mayRequest(catalogue.get(c), me, "engagement");
  const act = useWsAct();
  const suggestion = w.suggestion?.id ?? null;                           // the engine decides a suggestion by its id (wp.keep / wp.dismiss)
  const keep = () => request({ command: "wp.keep", engagementId: ws.id, payload: { suggestion }, what: `Keep ${w.ref} ${w.name}` });
  const dismiss = () => act.open({ command: "wp.dismiss", engagementId: ws.id, fixed: { suggestion }, title: `Dismiss ${w.ref} ${w.name}` });   // a reason is required
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <Link to={`/e/${ws.id}/wp/${encodeURIComponent(w.ref)}`} className="font-medium hover:underline">{showRef && <span className="font-mono text-xs text-muted">{w.ref} </span>}{w.name}</Link>
        {w.isNew && <span className="rounded bg-brand px-1.5 py-0.5 text-[10px] font-semibold uppercase text-brand-ink">New</span>}
        {w.stale && <StatusPill kind="stale" label="Out of date" title={w.staleWhy ?? undefined} />}
      </div>
      {w.suggestion && (
        <p className={`text-xs ${pending ? "text-agent" : "text-muted"}`}>
          {w.suggestion.by === "agent" ? <><span aria-hidden="true">✦ </span>Added by agent</> : <>Added by {w.suggestion.by === me.personName ? "you" : w.suggestion.by}</>}: {w.suggestion.reason}
          {w.suggestion.state !== "pending" && ` · ${w.suggestion.state}`}
        </p>
      )}
      {pending && (
        <div className="flex gap-1.5">
          <Button size="sm" kind="primary" disabled={!may("wp.keep").ok || suggestion === null} title={may("wp.keep").reason ?? undefined} aria-label={`Keep ${w.ref} ${w.name}`} onClick={keep}>Keep</Button>
          <Button size="sm" disabled={!may("wp.dismiss").ok || suggestion === null} title={may("wp.dismiss").reason ?? undefined} aria-label={`Dismiss ${w.ref} ${w.name}`} onClick={dismiss}>Dismiss</Button>
        </div>
      )}
      {act.drawer}
    </div>
  );
}
